/**
 * atexGate.tsx — the ATEX page, and the rule that governs everything on it.
 *
 * THE RULE, AND WHY IT IS IMPLEMENTED AS A DERIVATION RATHER THAN A RULE
 * ---------------------------------------------------------------------
 * An unassessed zone must never be rendered as a safe one. That is the single
 * most consequential thing this workbench is trusted not to get wrong, and the
 * reason it is dangerous is that the failure is invisible: a zone drawn in the
 * same calm green as an assessed one invites the reader to move on, and nobody
 * goes and check.
 *
 * So this component does not implement the rule by inspecting a status. It
 * implements it by ASKING THE MANIFEST what determination vocabulary exists, and
 * then refusing to draw anything at all unless the Engine published a per-value
 * assertion for the value in hand. Concretely:
 *
 *   • It scans the real manifest for a descriptor whose result schema publishes an
 *     enumerated property carrying the Engine's own per-value `x-presentation`
 *     assertion vocabulary. That is read through `readEnumeration`, the same
 *     reader the renderer uses, so the gate and the renderer cannot disagree about
 *     what the Engine published.
 *   • If NO descriptor publishes one, the gate says so, by name, and the page
 *     shows NO determination at all. Not a default, not an absence drawn as a
 *     value, not a neutral swatch that could be mistaken for an assessment. The
 *     area is left as an explicit absence.
 *   • If the Engine DOES publish one, the values it enumerates are listed with the
 *     Engine's own per-value title, and each is labelled with the assertion the
 *     Engine published for it — including the values for which the Engine
 *     published NO assertion, which are the ones that must never be drawn as
 *     settled. Those are the load-bearing rows on this page.
 *
 * The consequence on the integrated head is worth stating plainly: the Engine
 * publishes `operation/atex/legacy-migrate` and no determination capability at
 * all. This gate therefore refuses to render a determination, and the ATEX surface
 * names every determination capability it needs as not published. That is the
 * correct outcome, not a gap in this workbench — and it is the outcome the safety
 * rule demands, because a browser that drew a determination from nothing is
 * exactly the failure this file exists to prevent.
 */

import { useMemo } from "react";
import { allDescriptors, type CapabilityManifest, type JsonSchemaFragment } from "../../api/capabilityManifest";
import { Panel } from "../../components/Panel";
import { readEnumeration } from "../assessments/determination";

/** One value the Engine published, with the assertion the Engine published for it. */
export interface PublishedArm {
  readonly value: string;
  /** The Engine's own title for the value, when it published one. */
  readonly title: string | undefined;
  /** Exactly what the Engine asserted, or `undefined` when it asserted nothing. */
  readonly assertion: "affirmative" | "adverse" | undefined;
  /** Which descriptor, and which property, published this vocabulary. */
  readonly descriptorId: string;
  readonly propertyName: string;
}

export interface DeterminationVocabulary {
  /** Every arm the Engine published, across every descriptor that published one. */
  readonly arms: readonly PublishedArm[];
  /** The descriptors that published a determination vocabulary, by id. */
  readonly publishers: readonly string[];
}

/**
 * Read the determination vocabulary out of the manifest.
 *
 * A property qualifies when it publishes an enumeration AND at least one member
 * carries the Engine's own assertion metadata. That conjunction is what
 * distinguishes a safety vocabulary from any other enumeration: an enumeration
 * with no assertions decides nothing, and drawing one as though it did is the
 * failure this gate exists to prevent.
 */
export function readDeterminationVocabulary(manifest: CapabilityManifest): DeterminationVocabulary {
  const arms: PublishedArm[] = [];
  const publishers: string[] = [];
  for (const descriptor of allDescriptors(manifest)) {
    let publishedHere = false;
    for (const [propertyName, schema] of Object.entries(descriptor.resultSchema.properties ?? {})) {
      const enumeration = readEnumeration(schema as JsonSchemaFragment);
      if (enumeration === undefined) continue;
      const asserted = enumeration.values.filter(value =>
        typeof value === "string"
        && isRecord(enumeration.metadata.get(value))
        && enumeration.metadata.get(value)![VALUE_PRESENTATION] !== undefined);
      if (asserted.length === 0) continue;
      publishedHere = true;
      for (const value of enumeration.values) {
        if (typeof value !== "string") continue;
        const presentation = enumeration.metadata.get(value);
        const assertion = isRecord(presentation) ? presentation.assertion : undefined;
        arms.push({
          value,
          title: enumeration.titles.get(value),
          assertion: assertion === "affirmative" || assertion === "adverse" ? assertion : undefined,
          descriptorId: descriptor.id,
          propertyName,
        });
      }
    }
    if (publishedHere) publishers.push(descriptor.id);
  }
  return { arms, publishers: [...publishers].sort() };
}

const VALUE_PRESENTATION = "assertion";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The ATEX gate, rendered at the top of the surface that owns this question.
 *
 * When the Engine publishes no determination vocabulary, this renders an explicit
 * absence and says plainly that nothing on this page is an assessment. It is the
 * most important sentence in the whole workbench, and it is the one a future
 * Engine that DOES publish determinations will make false — which is why it is
 * derived from the manifest on every render rather than written down once.
 */
export function AtexDeterminationGate({ manifest }: { manifest: CapabilityManifest }): JSX.Element {
  const vocabulary = useMemo(() => readDeterminationVocabulary(manifest), [manifest]);

  if (vocabulary.arms.length === 0) {
    return (
      <Panel title="Determination" tone="warn" testId="atex-gate">
        <p className="bad" data-stage="atex-no-vocabulary">
          <strong>The Engine&apos;s manifest publishes no determination vocabulary.</strong> No capability it
          advertises declares a safety determination with a per-value assertion, so this workbench will not draw
          one.
        </p>
        <p>
          That is deliberate. A zone whose contents were never understood, and a zone nobody has assessed, are both
          ABSENCES of a determination. Rendering either of them the way this workbench renders an assessed zone would
          manufacture a safety claim the Engine has not made — and the failure would be silent, because a drawing
          that looks settled is the one nobody re-checks.
        </p>
        <p className="muted">
          Nothing below this panel is an assessment, and nothing on this page is coloured as though it were. The
          surface that owns this question names each determination capability it needs and reports which ones the
          Engine does not publish.
        </p>
      </Panel>
    );
  }

  const settled = vocabulary.arms.filter(arm => arm.assertion !== undefined);
  const unasserted = vocabulary.arms.filter(arm => arm.assertion === undefined);
  return (
    <Panel title="Determination" testId="atex-gate">
      <p className="muted" data-stage="atex-vocabulary-publishers">
        The Engine published a determination vocabulary on{" "}
        {vocabulary.publishers.map(id => <code key={id}>{id}</code>).reduce<React.ReactNode[]>(
          (acc, node, index) => (index === 0 ? [node] : [...acc, ", ", node]),
          [],
        )}
        .
      </p>
      <p>
        Only the {settled.length} value{settled.length === 1 ? "" : "s"} the Engine published an assertion for{" "}
        may ever be drawn as settled. The {unasserted.length} below assert nothing and are therefore never drawn that
        way, whatever value they carry.
      </p>
      <ul className="determinations" data-stage="atex-arms">
        {vocabulary.arms.map(arm => (
          <li
            key={`${arm.descriptorId}.${arm.propertyName}.${arm.value}`}
            className={arm.assertion === undefined ? "determination determination-indeterminate" : `determination determination-${arm.assertion}`}
            data-arm={arm.value}
            data-assertion={arm.assertion ?? "none"}
          >
            <span className="determination-name">{arm.value}</span>
            <span className="determination-statement">
              {arm.assertion === undefined
                ? "The Engine asserts nothing for this value. It decides nothing, and is never drawn as settled."
                : `The Engine published this determination as ${arm.assertion}.`}
            </span>
            {arm.title !== undefined && <span className="muted">{arm.title}</span>}
            <span className="muted"><code>{arm.descriptorId}</code> · <code>{arm.propertyName}</code></span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
